import { useMemo } from 'react'
import type { FC } from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import { Link, useLocation } from 'react-router'
import type { NavCategory, NavCategoryId, NavItem } from './navConfig'
import { useRouteBasePath } from '~/shared/hooks/useRouteBasePath'

interface PatientDetailNavStripProps {
  categories: { category: NavCategory; items: NavItem[] }[]
}

const findActiveCategoryId = (
  baseUrl: string,
  pathname: string,
  categories: { category: NavCategory; items: NavItem[] }[],
): NavCategoryId | null => {
  for (const { category, items } of categories) {
    if (items.some(item => pathname.startsWith(`${baseUrl}${item.to}`))) {
      return category.id
    }
  }
  return null
}

const PatientDetailNavStrip: FC<PatientDetailNavStripProps> = ({ categories }) => {
  const basePath = useRouteBasePath()
  const location = useLocation()
  const activeCategoryId = useMemo(
    () => findActiveCategoryId(basePath, location.pathname, categories),
    [basePath, location.pathname, categories],
  )

  const activeCategory = categories.find(c => c.category.id === activeCategoryId) ?? categories[0]
  const subItems = activeCategory?.items ?? []
  const activeSubItemValue =
    subItems.find(item => location.pathname.startsWith(`${basePath}${item.to}`))?.to ?? false

  // Underlined category tabs over pill sub-tabs. Both rows together stay within
  // the ~72px laptop-height budget.
  return (
    <Box sx={{ bgcolor: 'background.paper' }}>
      <Tabs
        value={activeCategoryId ?? false}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ minHeight: 34, px: 3, '& .MuiTabs-indicator': { height: '2.5px' } }}
      >
        {categories.map(({ category, items }) => {
          const firstItemPath = `${basePath}${items[0]?.to ?? ''}`
          return (
            <Tab
              key={category.id}
              value={category.id}
              label={category.label}
              component={Link}
              to={firstItemPath}
              sx={{
                minHeight: 34,
                minWidth: 0,
                px: 0.25,
                mr: 2.75,
                fontSize: '1.4rem',
                fontWeight: 600,
                textTransform: 'none',
                color: 'text.secondary',
                '&.Mui-selected': { color: 'primary.main' },
              }}
            />
          )
        })}
      </Tabs>
      <Tabs
        value={activeSubItemValue}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 38,
          px: 3,
          py: 0.5,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { alignItems: 'center', gap: 0.75 },
        }}
      >
        {subItems.map(item => (
          <Tab
            key={item.to}
            value={item.to}
            label={item.label}
            title={item.description}
            component={Link}
            to={`${basePath}${item.to}`}
            sx={theme => ({
              minHeight: 30,
              minWidth: 0,
              px: 1.875,
              py: 0.5,
              fontSize: '1.3rem',
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: 999,
              border: '1px solid transparent',
              color: 'text.secondary',
              '&:hover': { bgcolor: 'grey.50', color: 'text.primary' },
              '&.Mui-selected': {
                bgcolor: theme.palette.primary.lighter,
                borderColor: theme.palette.primary.light,
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            })}
          />
        ))}
      </Tabs>
    </Box>
  )
}

export default PatientDetailNavStrip
